/**
 * Zod schemas for every request body the API accepts. The server validates
 * with them; the client imports the inferred types so a renamed field breaks
 * the build instead of production.
 */
import { z } from 'zod';
import { CHARACTER_KINDS, CUSTOM_THEME_KEYS, FACTIONS, LOCALES, LOG_SOURCES, MAX_CHARACTER_LEVEL, MAX_PAGE_SIZE, PASSWORD_MIN_LENGTH, ROLES, THEMES, USERNAME_PATTERN, USER_ROLES, WOW_CLASSES, WOW_RACES, WOW_REGIONS, } from './constants.js';
/** Trimmed, non-empty, length-capped text. The workhorse of every form. */
const text = (max) => z.string().trim().min(1).max(max);
/** Optional free text: empty strings and `null` both mean "not set". */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional();
/**
 * Only http(s) links are stored. Anything else (`javascript:`, `data:`) would
 * end up in an href and become an XSS vector the moment it is rendered.
 */
export const httpUrl = z
    .string()
    .trim()
    .max(2048)
    .refine((value) => {
    try {
        return ['http:', 'https:'].includes(new URL(value).protocol);
    }
    catch {
        return false;
    }
}, 'must be an http(s) URL');
/**
 * An optional link. The empty-string branch comes first on purpose: a cleared
 * form field must normalise to `null` rather than fail the URL check.
 */
const optionalHttpUrl = z
    .union([z.literal(''), z.null(), httpUrl])
    .transform((value) => (value === '' || value === null ? null : value))
    .optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const hexColor = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'must be a hex colour');
/** A nullable integer field that accepts `null` to clear it. */
const nullableInt = (min, max) => z.number().int().min(min).max(max).nullable();
// ---------------------------------------------------------------------------
// Auth & account
// ---------------------------------------------------------------------------
export const loginSchema = z.object({
    username: z.string().trim().min(1).max(64),
    password: z.string().min(1).max(200),
});
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(200);
export const customThemeSchema = z
    .object(Object.fromEntries(CUSTOM_THEME_KEYS.map((key) => [key, hexColor.optional()])))
    .nullable();
export const updateProfileSchema = z
    .object({
    displayName: text(48).optional(),
    avatarUrl: optionalHttpUrl,
    locale: z.enum(LOCALES).optional(),
    theme: z.enum(THEMES).optional(),
    themeCustom: customThemeSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
// ---------------------------------------------------------------------------
// Admin: user management
// ---------------------------------------------------------------------------
export const createUserSchema = z.object({
    username: z.string().trim().regex(USERNAME_PATTERN, 'must be 3-32 letters, digits, . _ or -'),
    password: passwordSchema,
    displayName: text(48).optional(),
    role: z.enum(USER_ROLES).default('member'),
});
export const updateUserSchema = z
    .object({
    displayName: text(48).optional(),
    role: z.enum(USER_ROLES).optional(),
    isActive: z.boolean().optional(),
    /** Set by an admin without knowing the current one. */
    password: passwordSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------
export const playerInputSchema = z.object({
    name: text(48),
    userId: z.number().int().positive().nullable().optional(),
    bio: optionalText(1000),
    mainCharacterId: z.number().int().positive().nullable().optional(),
});
export const playerUpdateSchema = playerInputSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
export const characterStatsSchema = z.object({
    itemLevel: nullableInt(0, 1000).optional(),
    mythicPlusScore: nullableInt(0, 10_000).optional(),
    bestKeyLevel: nullableInt(0, 100).optional(),
    progressPercent: nullableInt(0, 100).optional(),
    achievementPoints: nullableInt(0, 100_000).optional(),
    pvpRating: nullableInt(0, 5000).optional(),
});
export const characterInputSchema = z.object({
    playerId: z.number().int().positive(),
    name: text(24),
    realm: text(48),
    // Nullable: a quick-added character has no class until its first sync from
    // the Armory fills one in.
    class: z.enum(WOW_CLASSES).nullable().optional(),
    spec: optionalText(32),
    race: z.enum(WOW_RACES).nullable().optional(),
    faction: z.enum(FACTIONS).default('neutral'),
    level: z.number().int().min(1).max(MAX_CHARACTER_LEVEL).default(MAX_CHARACTER_LEVEL),
    role: z.enum(ROLES).nullable().optional(),
    kind: z.enum(CHARACTER_KINDS).default('main'),
    stats: characterStatsSchema.optional(),
    notes: optionalText(1000),
    profileUrl: optionalHttpUrl,
});
export const characterUpdateSchema = characterInputSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
/**
 * The normal way to add a character: just enough to look it up on the
 * Armory. Everything else — class, spec, race, faction, level, item level —
 * is filled in by the sync that runs right after creation.
 */
export const characterQuickAddSchema = z.object({
    name: text(24),
    realm: text(48),
});
/** Attaches (or, with `null`, removes) a character's hero GIF/clip — an already-uploaded, unclaimed media asset. */
export const characterMediaSchema = z.object({
    mediaId: z.number().int().positive().nullable(),
});
export const characterLogInputSchema = z.object({
    source: z.enum(LOG_SOURCES).default('warcraftlogs'),
    url: httpUrl,
    title: text(140),
    playedOn: isoDate.nullable().optional(),
    notes: optionalText(500),
});
export const characterLogUpdateSchema = characterLogInputSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------
/**
 * An attachment is submitted either as an already-uploaded `mediaId` or as an
 * external `url`. Exactly one, so the server never has to guess which wins.
 */
export const attachmentInputSchema = z
    .object({
    mediaId: z.number().int().positive().optional(),
    url: httpUrl.optional(),
    title: optionalText(140),
})
    .refine((value) => (value.mediaId === undefined) !== (value.url === undefined), 'provide exactly one of mediaId or url');
/** Tags are normalised to lowercase so `#Mythic` and `#mythic` are one tag. */
const tagsSchema = z
    .array(z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[\p{L}\p{N}][\p{L}\p{N}_-]{0,23}$/u, 'invalid tag'))
    .max(8)
    .transform((tags) => [...new Set(tags)]);
export const postInputSchema = z.object({
    title: text(140),
    body: optionalText(8000),
    tags: tagsSchema.optional(),
    attachments: z.array(attachmentInputSchema).max(12).optional(),
    isPinned: z.boolean().optional(),
});
export const postUpdateSchema = postInputSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
export const commentInputSchema = z.object({ body: text(4000) });
/**
 * Reactions are a short allowlist-free emoji field: 1-8 code points so a
 * "reaction" cannot smuggle in a paragraph of text.
 */
export const reactionInputSchema = z.object({
    emoji: z
        .string()
        .trim()
        .min(1)
        .refine((value) => [...value].length <= 8, 'too long'),
});
// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
export const siteSettingsSchema = z
    .object({
    /** 128 MiB floor: below that the app cannot hold anything useful. */
    storageLimitBytes: z
        .number()
        .int()
        .min(128 * 1024 * 1024)
        .max(2 * 1024 ** 4)
        .optional(),
    siteTitle: text(64).optional(),
    publicRoster: z.boolean().optional(),
    wowRegion: z.enum(WOW_REGIONS).optional(),
    warcraftlogsZoneId: z.number().int().positive().nullable().optional(),
    defaultTheme: z.enum(THEMES).optional(),
    defaultThemeCustom: customThemeSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'nothing to update');
// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------
/** Query params arrive as strings, so coerce rather than reject. */
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20),
});
export const feedQuerySchema = paginationSchema.extend({
    tag: z.string().trim().toLowerCase().max(24).optional(),
    authorId: z.coerce.number().int().positive().optional(),
    search: z.string().trim().max(120).optional(),
});
//# sourceMappingURL=schemas.js.map